/**
 * PATH: packages/email-hub/src/feature/components/ParkedReviewDigest.tsx
 * FILE_ID: EMAILHUB-FEATURE-COMPONENTS-PARKEDDIGEST-001
 * PURPOSE: ONE roll-up strip at the top of the inbox list — "AI parked
 *          N for review" — replacing the per-email amber prose sentence
 *          (triage v6). Clicking it FILTERS the inbox to those emails.
 * WHY:     Operator (2026-07-09): "that low confidence is just spamming
 *          here". Review-parked state surfaces ONCE as a digest.
 *          Operator (2026-07-26): parked mail felt "removed from view";
 *          the answer is a filter over the inbox, not a separate place.
 *          Do NOT remount LowConfidenceDigest above this strip — that
 *          stack shipped in #149 and was killed in #306 (2026-07-26).
 * RELATED:
 *   - AiTriageBanner.tsx: describeReviewHint (the hover-only audit line).
 *   - InboxPage.tsx: host — passes the stat count + the filter toggle.
 */
import { CircleDashed, X } from 'lucide-react'
import type { Email } from '../hooks/useEmails.types'

/** Pure I → O: the visible emails that are review-parked (AI suggested,
 *  low confidence, awaiting a human ✓/✗).
 *
 *  Retained as the FALLBACK count only. It can only see the rows already
 *  loaded (~200), so on a large mailbox it under-reports whenever parked mail
 *  sits past the first page — which is exactly how a "3" could sit above an
 *  inbox holding 18. Prefer the `count` prop, which the backend computes
 *  org-wide. */
export function parkedForReview(emails: readonly Email[]): Email[] {
  return emails.filter(
    (e) =>
      e.allocation_summary?.needs_review === true &&
      e.allocation_summary?.source === 'AI_SUGGESTED_PENDING',
  )
}

export interface ParkedReviewDigestProps {
  emails: readonly Email[]
  /** Open one parked email (the host's normal row-open path). */
  onOpen: (email: Email) => void
  /** Authoritative org-wide count from ``/api/emails/stats/`` →
   *  ``needs_review``. Undefined while stats are loading or when served from
   *  a cached pre-2026-07-26 payload, in which case the page-local count is
   *  used so the strip degrades instead of vanishing. */
  count?: number
  /** True when the inbox is already filtered to review-parked mail. */
  active?: boolean
  /** Toggle ``?needs_review=true`` on the inbox. Absent = no filter UI
   *  (keeps the component usable in hosts that have no filter, and keeps
   *  every existing call site valid). */
  onToggleFilter?: () => void
}

/** The inbox-header digest. Renders nothing when nothing is parked, so a
 *  clean inbox stays clean — EXCEPT while the filter is active, where it must
 *  stay mounted even at zero or the operator is stranded in an empty list
 *  with no way back out. */
export function ParkedReviewDigest({
  emails,
  onOpen,
  count,
  active = false,
  onToggleFilter,
}: ParkedReviewDigestProps) {
  const parked = parkedForReview(emails)
  // Prefer the server count; fall back to what is on screen. `?? ` not `||`:
  // a legitimate 0 from the server must not silently fall through to the
  // page-local number.
  const total = count ?? parked.length
  if (total === 0 && !active) return null

  return (
    <div
      data-testid="parked-review-digest"
      className="px-3 py-1.5 border-b border-amber-300/50 dark:border-amber-500/25 bg-amber-50/70 dark:bg-amber-500/10 flex items-center gap-2 text-[11px]"
    >
      <CircleDashed className="h-3.5 w-3.5 text-amber-600 dark:text-amber-400 shrink-0" aria-hidden />
      <span
        className="text-amber-800 dark:text-amber-200 font-medium"
        data-testid="parked-review-count"
      >
        {active
          ? `Showing ${total} email${total === 1 ? '' : 's'} in AI review`
          : `AI parked ${total} email${total === 1 ? '' : 's'} for review`}
      </span>

      {onToggleFilter ? (
        <button
          type="button"
          onClick={onToggleFilter}
          aria-pressed={active}
          data-testid="parked-review-filter-toggle"
          className="ml-auto inline-flex items-center gap-1 rounded-md border border-amber-400/60 px-2 py-0.5 text-[10.5px] font-medium text-amber-800 dark:text-amber-200 hover:bg-amber-100/70 dark:hover:bg-amber-500/20 transition-colors"
        >
          {active ? (
            <>
              <X className="h-3 w-3" aria-hidden />
              Clear filter
            </>
          ) : (
            'Review these'
          )}
        </button>
      ) : (
        // Legacy host with no filter wiring: keep the original affordance
        // rather than rendering a strip the operator cannot act on.
        parked.length > 0 && (
          <button
            type="button"
            onClick={() => onOpen(parked[0])}
            className="ml-auto rounded-md border border-amber-400/60 px-2 py-0.5 text-[10.5px] font-medium text-amber-800 dark:text-amber-200 hover:bg-amber-100/70 dark:hover:bg-amber-500/20 transition-colors"
          >
            Review first
          </button>
        )
      )}
    </div>
  )
}

export default ParkedReviewDigest
